import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import api from '../../services/api';
import { errorAlert } from '../../utils/alerts';

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const AccessLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const [filters, setFilters] = useState({
    user: '',
    success: '',
    date_from: '',
    date_to: '',
  });

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users/');
      setUsers(res.data.results || res.data);
    } catch {
      // filtro de usuário fica indisponível; logs ainda carregam
    }
  };

  const fetchLogs = async (pageToLoad = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      const params = { page: pageToLoad };
      if (currentFilters.user) params.user = currentFilters.user;
      if (currentFilters.success) params.success = currentFilters.success;
      if (currentFilters.date_from) params.date_from = currentFilters.date_from;
      if (currentFilters.date_to) params.date_to = currentFilters.date_to;

      const res = await api.get('/access-logs/', { params });
      const data = res.data;
      setLogs(data.results || data);
      setHasNext(Boolean(data.next));
      setHasPrevious(Boolean(data.previous));
      setPage(pageToLoad);
    } catch {
      errorAlert('Erro ao carregar', 'Não foi possível carregar os logs de acesso.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchLogs(1, filters);
  };

  const clearFilters = () => {
    const cleared = { user: '', success: '', date_from: '', date_to: '' };
    setFilters(cleared);
    fetchLogs(1, cleared);
  };

  const selectClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white';

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <form
        onSubmit={applyFilters}
        className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end"
      >
        <div>
          <label htmlFor="f_user" className="block text-xs font-medium text-gray-500 mb-1">Usuário</label>
          <select id="f_user" name="user" value={filters.user} onChange={handleFilterChange} className={selectClass}>
            <option value="">Todos</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f_success" className="block text-xs font-medium text-gray-500 mb-1">Resultado</label>
          <select id="f_success" name="success" value={filters.success} onChange={handleFilterChange} className={selectClass}>
            <option value="">Todos</option>
            <option value="true">Sucesso</option>
            <option value="false">Falha</option>
          </select>
        </div>
        <div>
          <label htmlFor="f_from" className="block text-xs font-medium text-gray-500 mb-1">Data de</label>
          <input id="f_from" name="date_from" type="date" value={filters.date_from} onChange={handleFilterChange} className={selectClass} />
        </div>
        <div>
          <label htmlFor="f_to" className="block text-xs font-medium text-gray-500 mb-1">Data até</label>
          <input id="f_to" name="date_to" type="date" value={filters.date_to} onChange={handleFilterChange} className={selectClass} />
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Filter size={15} />
            Filtrar
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Limpar
          </button>
        </div>
      </form>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuário</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data/Hora</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">IP</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User-Agent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="inline-block w-7 h-7 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-400 text-sm">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2.5">
                        <div>
                          <div className="font-medium text-gray-900">
                            {log.user?.nome || log.user?.username || log.username_attempt || '-'}
                          </div>
                          {log.user && log.user.nome && (
                            <div className="text-gray-500 text-xs">@{log.user.username}</div>
                          )}
                        </div>
                        {log.success ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                            Sucesso
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                            Falha
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{formatDateTime(log.timestamp)}</td>
                    <td className="px-6 py-4 text-gray-600">{log.ip || '-'}</td>
                    <td className="px-6 py-4 text-gray-600">
                      <span className="inline-block max-w-xs truncate align-middle" title={log.user_agent || ''}>
                        {log.user_agent || '-'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">Página {page}</p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!hasPrevious || loading}
              onClick={() => fetchLogs(page - 1)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={15} />
              Anterior
            </button>
            <button
              type="button"
              disabled={!hasNext || loading}
              onClick={() => fetchLogs(page + 1)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Próximo
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessLogs;
